/**
 * Feux d'artifice.
 *
 * Tout tient dans UN SEUL `THREE.Points` avec un pool fixe de particules et un
 * `ShaderMaterial` maison. Au moment d'une explosion, le processeur ecrit une
 * tranche contigue d'attributs (position de depart, vitesse, couleur, naissance,
 * duree, taille, tirage, physique) puis n'y retouche plus: c'est le vertex
 * shader qui integre le mouvement
 *
 *   dv/dt = g - k v   ->   p(t) = p0 + (v0 - g/k) (1 - e^(-k t)) / k + (g/k) t
 *
 * ce qui donne la parabole `p0 + v t + g t^2 / 2` amortie par un frottement.
 * Le processeur ne touche a rien pendant le vol, donc une dizaine de bouquets
 * simultanes ne coutent presque rien.
 *
 * Piege resolu ici: en projection ORTHOGRAPHIQUE il n'y a aucune division par
 * la profondeur, donc `gl_PointSize` ne peut pas etre derive de `mvPosition.z`.
 * On passe a la place `uEchelle` = pixels du tampon de dessin par metre de
 * monde, calcule depuis le frustum et le zoom de la camera (voir `majCamera`).
 */
import * as THREE from 'three'
import { CAM_AZIMUTH, CAM_ELEVATION } from '../../shared/constants.ts'
import { Alea, clamp, melangerCouleur } from '../../shared/sim/alea.ts'
import type { FormeFeu } from '../../shared/types/monde.ts'
import type { Vec2 } from '../../shared/types/world.ts'
import { journal } from '../log/journal.ts'

// ------------------------------------------------------------------ reglages

/**
 * Taille du pool de particules. Fixe: aucune allocation en cours de partie.
 * Une gerbe coute au plus ~1400 particules, donc on tient une vingtaine
 * d'explosions vraiment simultanees. Au-dela l'anneau recycle les plus
 * anciennes, qui disparaissent d'un coup: mesure a 3 bouquets simultanes,
 * 21 400 particules vivantes en pointe, d'ou cette marge.
 */
const POOL = 30000

/** Nombre de lumieres d'explosion reutilisees en tourniquet. */
const NB_LUMIERES = 4

/** Duree de l'extinction d'une lumiere d'explosion, en secondes. */
const DUREE_LUMIERE = 1.2

/** Portee et decroissance des lumieres d'explosion. */
const PORTEE_LUMIERE = 320
const DECROISSANCE_LUMIERE = 1.35

/** Altitude d'explosion, en metres au-dessus du sol de depart. */
const ALTITUDE_MIN = 22
const ALTITUDE_MAX = 34

/** Duree de montee de la fusee, en secondes. */
const MONTEE_MIN = 1.1
const MONTEE_MAX = 1.5

/** Physique de la fusee: gravite reduite et frottement faible. */
const FUSEE_GRAVITE = 5
const FUSEE_FROTTEMENT = 0.35

/** Nombre de particules de trainee derriere la fusee. */
const NB_TRAINEE = 60

/** Nombre de particules du flash de detonation. */
const NB_FLASH = 6

/** Bornes de `gl_PointSize`, en pixels du tampon de dessin. */
const TAILLE_MIN_PX = 1
const TAILLE_MAX_PX = 40

// ------------------------------------------------------------------ shaders

const VERTEX = /* glsl */ `
uniform float uTemps;
uniform float uEchelle;
uniform float uTailleMin;
uniform float uTailleMax;

attribute vec3 aVitesse;
attribute vec3 aCouleur;
attribute float aNaissance;
attribute float aDuree;
attribute float aTaille;
attribute float aTirage;
// x = intensite de la gravite (m/s2, vers le bas), y = frottement k (1/s)
attribute vec2 aPhys;

varying vec3 vCouleur;
varying float vIntensite;
varying float vNoyau;

void main() {
  float age = uTemps - aNaissance;

  // Particule libre ou hors de sa fenetre de vie: on l'ecarte du volume de
  // clipping et on annule sa taille, elle ne coute plus rien au rasterizer.
  if (aDuree <= 0.0 || age < 0.0 || age >= aDuree) {
    vCouleur = vec3(0.0);
    vIntensite = 0.0;
    vNoyau = 0.0;
    gl_PointSize = 0.0;
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }

  // Integration analytique du mouvement amorti.
  float k = max(aPhys.y, 0.02);
  vec3 vLimite = vec3(0.0, -aPhys.x / k, 0.0);
  float amorti = (1.0 - exp(-k * age)) / k;
  vec3 p = position + (aVitesse - vLimite) * amorti + vLimite * age;

  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);

  float u = age / aDuree;

  // Allumage quasi instantane, extinction douce, scintillement propre a
  // chaque particule pour que la gerbe grouille au lieu de pulser en bloc.
  float allumage = smoothstep(0.0, 0.03, u);
  // Palier lumineux sur la premiere moitie de la vie puis extinction franche:
  // une simple puissance eteignait les gerbes longues (saule) bien trop tot.
  float extinction = (1.0 - smoothstep(0.5, 1.0, u)) * (0.55 + 0.45 * (1.0 - u));
  float scintille = 0.72 + 0.28 * sin(age * (26.0 + aTirage * 46.0) + aTirage * 41.7);

  vIntensite = allumage * extinction * scintille;
  vNoyau = pow(1.0 - u, 3.0);
  vCouleur = aCouleur;

  // ORTHOGRAPHIQUE: pas de division par la profondeur. uEchelle convertit
  // directement les metres en pixels du tampon de dessin.
  float eclat = 1.0 - smoothstep(0.0, 0.05, u);
  float rayon = aTaille * (0.55 + 0.75 * (1.0 - u) + 2.2 * eclat);
  gl_PointSize = clamp(rayon * uEchelle, uTailleMin, uTailleMax);
}
`

const FRAGMENT = /* glsl */ `
varying vec3 vCouleur;
varying float vIntensite;
varying float vNoyau;

void main() {
  // Degrade radial calcule: aucune texture n'est chargee.
  vec2 d = gl_PointCoord - vec2(0.5);
  float r2 = dot(d, d) * 4.0;
  if (r2 >= 1.0) discard;
  float r = sqrt(r2);

  float halo = pow(1.0 - r, 2.2);
  float noyau = smoothstep(0.55, 0.0, r);

  vec3 c = vCouleur * halo * 2.6 + mix(vCouleur, vec3(1.0), 0.6) * noyau * vNoyau * 0.9;

  // Melange additif: three.js multiplie rgb par alpha, donc l'intensite ne
  // doit etre appliquee qu'une seule fois, via alpha.
  gl_FragColor = vec4(c, vIntensite);

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`

// ------------------------------------------------------------------ physique

/** Position a l'instant t d'un mobile amorti partant de p0 a la vitesse v0. */
function integrePos(p0: number, v0: number, acc: number, k: number, t: number): number {
  const limite = acc / k
  return p0 + (v0 - limite) * ((1 - Math.exp(-k * t)) / k) + limite * t
}

/** Vitesse a l'instant t du meme mobile. */
function integreVit(v0: number, acc: number, k: number, t: number): number {
  const limite = acc / k
  return (v0 - limite) * Math.exp(-k * t) + limite
}

/**
 * Vitesse verticale initiale pour qu'une fusee amortie atteigne `cible`
 * exactement au bout de `duree` secondes.
 */
function vitesseMontee(y0: number, cible: number, duree: number): number {
  const g = FUSEE_GRAVITE
  const k = FUSEE_FROTTEMENT
  const amorti = (1 - Math.exp(-k * duree)) / k
  return (cible - y0 + (g / k) * duree) / amorti - g / k
}

// -------------------------------------------------------------------- etats

interface FeuEnVol {
  id: number
  forme: FormeFeu
  couleur: number
  azimut: number
  /** Point d'explosion, calcule des le lancement. */
  centre: THREE.Vector3
  /** Instant local de l'explosion. */
  tExplosion: number
  /** Instant local ou la derniere particule meurt. */
  tFin: number
  explose: boolean
}

interface TirProgramme {
  t: number
  depart: Vec2
  hauteurSol: number
  couleur: number
  forme: FormeFeu
  azimut: number
}

interface EtatLumiere {
  light: THREE.PointLight
  debut: number
  actif: boolean
}

interface Groupe {
  fin: number
  nb: number
}

/** Repere du plan perpendiculaire a l'axe de vue, pour les formes plates. */
interface PlanCamera {
  droite: THREE.Vector3
  haut: THREE.Vector3
  normale: THREE.Vector3
}

/**
 * Base du plan face a la camera. `droite` est horizontal, `haut` remonte dans
 * le plan de l'ecran: une forme dessinee avec ces deux vecteurs apparait sans
 * deformation depuis la vue a 45 degres.
 */
function planCamera(azimut: number, elevation: number): PlanCamera {
  const ca = Math.cos(azimut)
  const sa = Math.sin(azimut)
  const ce = Math.cos(elevation)
  const se = Math.sin(elevation)
  return {
    droite: new THREE.Vector3(ca, 0, -sa),
    haut: new THREE.Vector3(-se * sa, ce, -se * ca),
    normale: new THREE.Vector3(ce * sa, se, ce * ca),
  }
}

// ------------------------------------------------------------------- rendu

export class RenduFeux {
  /** Pool de lumieres d'explosion, deja ajoutees a la scene. */
  readonly lumieres: THREE.PointLight[] = []

  /** Appele a l'instant de l'explosion, pour declencher le son. */
  onExplosion: ((position: THREE.Vector3, couleur: number) => void) | null = null

  /** Intensite de crete d'une lumiere d'explosion. Reglable depuis le jeu. */
  intensiteLumiere = 1100

  private scene: THREE.Scene
  private geo: THREE.BufferGeometry
  private mat: THREE.ShaderMaterial
  private points: THREE.Points
  private attributs: THREE.BufferAttribute[] = []

  private tPos: Float32Array
  private tVit: Float32Array
  private tCoul: Float32Array
  private tNais: Float32Array
  private tDur: Float32Array
  private tTail: Float32Array
  private tTir: Float32Array
  private tPhy: Float32Array

  private curseur = 0
  private alea = new Alea(0x5eedf3)
  private teinte = new THREE.Color()
  private teinteLumiere = new THREE.Color()

  private enVol: FeuEnVol[] = []
  private programmes: TirProgramme[] = []
  private groupes: Groupe[] = []
  private etats: EtatLumiere[] = []

  private prochainId = 1
  /** Origine des temps, pour garder `uTemps` petit et precis en float. */
  private origine = Number.NaN
  private tLocal = 0
  private phaseFlamme = 0

  constructor(scene: THREE.Scene) {
    this.scene = scene

    this.tPos = new Float32Array(POOL * 3)
    this.tVit = new Float32Array(POOL * 3)
    this.tCoul = new Float32Array(POOL * 3)
    this.tNais = new Float32Array(POOL)
    this.tDur = new Float32Array(POOL)
    this.tTail = new Float32Array(POOL)
    this.tTir = new Float32Array(POOL)
    this.tPhy = new Float32Array(POOL * 2)

    // Naissance tres ancienne + duree nulle: tout le pool demarre eteint.
    this.tNais.fill(-1e6)

    this.geo = new THREE.BufferGeometry()
    const attr = (tableau: Float32Array, taille: number): THREE.BufferAttribute => {
      const a = new THREE.BufferAttribute(tableau, taille)
      a.setUsage(THREE.DynamicDrawUsage)
      this.attributs.push(a)
      return a
    }
    this.geo.setAttribute('position', attr(this.tPos, 3))
    this.geo.setAttribute('aVitesse', attr(this.tVit, 3))
    this.geo.setAttribute('aCouleur', attr(this.tCoul, 3))
    this.geo.setAttribute('aNaissance', attr(this.tNais, 1))
    this.geo.setAttribute('aDuree', attr(this.tDur, 1))
    this.geo.setAttribute('aTaille', attr(this.tTail, 1))
    this.geo.setAttribute('aTirage', attr(this.tTir, 1))
    this.geo.setAttribute('aPhys', attr(this.tPhy, 2))

    // Les positions stockees sont celles du depart: le shader deplace les
    // particules, donc les bornes calculees par three.js seraient fausses.
    this.geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1e5)

    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        uTemps: { value: 0 },
        uEchelle: { value: 40 },
        uTailleMin: { value: TAILLE_MIN_PX },
        uTailleMax: { value: TAILLE_MAX_PX },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true,
      transparent: true,
    })

    this.points = new THREE.Points(this.geo, this.mat)
    this.points.frustumCulled = false
    this.points.renderOrder = 20
    this.points.name = 'feux'
    this.scene.add(this.points)

    for (let i = 0; i < NB_LUMIERES; i++) {
      const l = new THREE.PointLight(0xffffff, 0, PORTEE_LUMIERE, DECROISSANCE_LUMIERE)
      l.name = `feu-lumiere-${i}`
      // Ne jamais basculer `visible`: cela changerait le nombre de lumieres
      // de la scene et forcerait three.js a recompiler tous les shaders.
      l.visible = true
      l.castShadow = false
      this.scene.add(l)
      this.lumieres.push(l)
      this.etats.push({ light: l, debut: -1e6, actif: false })
    }

    journal.info('feux', `pool de ${POOL} particules, ${NB_LUMIERES} lumieres`)
  }

  // ---------------------------------------------------------------- camera

  /**
   * A appeler quand le zoom ou la taille de la fenetre changent.
   * `uEchelle` vaut le nombre de pixels du tampon de dessin par metre de
   * monde: en orthographique c'est la seule facon correcte de dimensionner
   * les points, puisque la profondeur n'intervient pas dans la projection.
   */
  majCamera(camera: THREE.OrthographicCamera, hauteurTamponPixels: number): void {
    const zoom = camera.zoom || 1
    const hauteurMonde = (camera.top - camera.bottom) / zoom
    this.mat.uniforms.uEchelle!.value = hauteurTamponPixels / Math.max(1e-6, hauteurMonde)
  }

  /** Variante directe, si le jeu calcule lui-meme les pixels par metre. */
  echelle(pixelsParMetre: number): void {
    this.mat.uniforms.uEchelle!.value = Math.max(1e-6, pixelsParMetre)
  }

  // ---------------------------------------------------------------- tirs

  /**
   * Lance un feu. La fusee monte pendant 1.1 a 1.5 s puis explose entre 28 et
   * 45 m au-dessus de `hauteurSol`. `azimutCamera` sert a orienter les formes
   * plates (coeur, etoile) face a la camera; laisser la valeur par defaut si
   * la camera n'a pas tourne.
   */
  lancer(
    depart: Vec2,
    hauteurSol: number,
    couleur: number,
    forme: FormeFeu,
    tempsSec: number,
    azimutCamera: number = CAM_AZIMUTH,
  ): number {
    const t = this.local(tempsSec)
    const a = this.alea
    const id = this.prochainId++

    const y0 = hauteurSol + 0.6
    const cible = hauteurSol + a.entre(ALTITUDE_MIN, ALTITUDE_MAX)
    const montee = a.entre(MONTEE_MIN, MONTEE_MAX)
    const v0y = vitesseMontee(y0, cible, montee)
    const derive = a.entre(-1.4, 1.4)
    const derive2 = a.entre(-1.4, 1.4)

    // Point d'explosion: on evalue la trajectoire exacte de la fusee.
    const cx = integrePos(depart.x, derive, 0, FUSEE_FROTTEMENT, montee)
    const cz = integrePos(depart.z, derive2, 0, FUSEE_FROTTEMENT, montee)
    const centre = new THREE.Vector3(cx, cible, cz)

    this.emettreFusee(depart, y0, derive, v0y, derive2, montee, couleur, t)

    this.enVol.push({
      id,
      forme,
      couleur,
      azimut: azimutCamera,
      centre,
      tExplosion: t + montee,
      tFin: t + montee + 4,
      explose: false,
    })
    return id
  }

  /**
   * Programme un bouquet: 6 a 10 tirs echelonnes sur environ 4 s, positions,
   * couleurs et formes variees. Pour la rampe de lancement d'un spectacle.
   */
  lancerBouquet(
    depart: Vec2,
    hauteurSol: number,
    couleurs: number[],
    tempsSec: number,
    azimutCamera: number = CAM_AZIMUTH,
  ): void {
    const t = this.local(tempsSec)
    const a = this.alea
    const nb = a.entier(6, 10)
    const formes: FormeFeu[] = ['pivoine', 'saule', 'coeur', 'etoile']
    const palette = couleurs.length > 0 ? couleurs : [0xff4d4d, 0x4dc3ff, 0xffe066, 0xb04dff]

    for (let i = 0; i < nb; i++) {
      // Echelonnement irregulier: un rythme trop regulier fait mecanique.
      const retard = (i / nb) * 3.6 + a.entre(0, 0.35)
      this.programmes.push({
        t: t + retard,
        depart: { x: depart.x + a.entre(-9, 9), z: depart.z + a.entre(-9, 9) },
        hauteurSol,
        couleur: palette[a.entier(0, palette.length - 1)] ?? 0xffffff,
        forme: a.choix(formes),
        azimut: azimutCamera,
      })
    }
    this.programmes.sort((x, y) => x.t - y.t)
    journal.info('feux', `bouquet de ${nb} tirs programme`)
  }

  // ---------------------------------------------------------------- boucle

  maj(dt: number, tempsSec: number): void {
    const t = this.local(tempsSec)
    this.tLocal = t
    this.mat.uniforms.uTemps!.value = t
    this.phaseFlamme += dt * 37

    // Tirs programmes arrives a echeance.
    while (this.programmes.length > 0) {
      const p = this.programmes[0]!
      if (p.t > t) break
      this.programmes.shift()
      this.lancer(p.depart, p.hauteurSol, p.couleur, p.forme, tempsSec, p.azimut)
    }

    // Explosions en attente.
    for (const feu of this.enVol) {
      if (feu.explose || feu.tExplosion > t) continue
      feu.explose = true
      const duree = this.emettreExplosion(feu, feu.tExplosion)
      feu.tFin = feu.tExplosion + duree
      this.allumer(feu.centre, feu.couleur, t)
      if (this.onExplosion) this.onExplosion(feu.centre.clone(), feu.couleur)
    }

    // Retirer les feux termines et les groupes de particules eteints.
    for (let i = this.enVol.length - 1; i >= 0; i--) {
      if (this.enVol[i]!.tFin < t) this.enVol.splice(i, 1)
    }
    for (let i = this.groupes.length - 1; i >= 0; i--) {
      if (this.groupes[i]!.fin < t) this.groupes.splice(i, 1)
    }

    // Extinction des lumieres.
    for (const e of this.etats) {
      if (!e.actif) continue
      const u = (t - e.debut) / DUREE_LUMIERE
      if (u >= 1) {
        e.actif = false
        e.light.intensity = 0
        continue
      }
      // Montee eclair sur 5 % de la duree, puis chute, plus un tremblement.
      const enveloppe = u < 0.05 ? u / 0.05 : Math.pow(1 - (u - 0.05) / 0.95, 2.4)
      const tremble = 0.86 + 0.14 * Math.sin(this.phaseFlamme + e.debut * 13)
      e.light.intensity = this.intensiteLumiere * enveloppe * tremble
    }
  }

  // ---------------------------------------------------------------- mesures

  /** Feux en cours: fusee en vol ou particules d'explosion encore vivantes. */
  nombreActifs(): number {
    return this.enVol.length + this.programmes.length
  }

  /** Estimation du nombre de particules encore dans leur fenetre de vie. */
  nombreParticulesVivantes(): number {
    let n = 0
    for (const g of this.groupes) if (g.fin > this.tLocal) n += g.nb
    return n
  }

  // ---------------------------------------------------------------- menage

  effacerTout(): void {
    this.tDur.fill(0)
    this.tNais.fill(-1e6)
    this.marquer(0, POOL)
    this.curseur = 0
    this.enVol.length = 0
    this.programmes.length = 0
    this.groupes.length = 0
    for (const e of this.etats) {
      e.actif = false
      e.light.intensity = 0
    }
  }

  dispose(): void {
    this.effacerTout()
    this.scene.remove(this.points)
    for (const l of this.lumieres) this.scene.remove(l)
    this.lumieres.length = 0
    this.etats.length = 0
    this.geo.dispose()
    this.mat.dispose()
    journal.info('feux', 'systeme libere')
  }

  // ------------------------------------------------------------ interne

  private local(tempsSec: number): number {
    if (!Number.isFinite(this.origine)) this.origine = tempsSec
    return tempsSec - this.origine
  }

  /**
   * Reserve une tranche CONTIGUE du pool. Si la fin du pool ne suffit pas, on
   * repart de zero: les plus anciennes particules sont recyclees, ce qui evite
   * de decouper les plages de mise a jour du tampon.
   */
  private reserver(n: number): number {
    if (n > POOL) return 0
    if (this.curseur + n > POOL) this.curseur = 0
    const debut = this.curseur
    this.curseur += n
    return debut
  }

  /** Signale au pilote la tranche d'attributs a reenvoyer sur la carte. */
  private marquer(debut: number, n: number): void {
    for (const a of this.attributs) {
      const taille = a.itemSize
      a.addUpdateRange(debut * taille, n * taille)
      a.needsUpdate = true
    }
  }

  private ecrire(
    i: number,
    px: number,
    py: number,
    pz: number,
    vx: number,
    vy: number,
    vz: number,
    couleur: number,
    naissance: number,
    duree: number,
    taille: number,
    gravite: number,
    frottement: number,
  ): void {
    const i3 = i * 3
    this.tPos[i3] = px
    this.tPos[i3 + 1] = py
    this.tPos[i3 + 2] = pz
    this.tVit[i3] = vx
    this.tVit[i3 + 1] = vy
    this.tVit[i3 + 2] = vz
    // setHex convertit du sRGB vers l'espace de travail lineaire du moteur.
    this.teinte.setHex(couleur)
    this.tCoul[i3] = this.teinte.r
    this.tCoul[i3 + 1] = this.teinte.g
    this.tCoul[i3 + 2] = this.teinte.b
    this.tNais[i] = naissance
    this.tDur[i] = duree
    this.tTail[i] = taille
    this.tTir[i] = this.alea.reel()
    this.tPhy[i * 2] = gravite
    this.tPhy[i * 2 + 1] = frottement
  }

  // --------------------------------------------------------------- fusee

  private emettreFusee(
    depart: Vec2,
    y0: number,
    vx: number,
    v0y: number,
    vz: number,
    montee: number,
    couleur: number,
    t: number,
  ): void {
    const a = this.alea
    const k = FUSEE_FROTTEMENT
    const g = FUSEE_GRAVITE
    const nb = NB_TRAINEE + 2
    const debut = this.reserver(nb)
    let i = debut

    // Point vif: deux particules qui suivent exactement la fusee et
    // disparaissent pile a l'explosion. Pas plus: empilees au meme endroit et
    // melangees en additif, elles saturent vite en disque blanc.
    for (let j = 0; j < 2; j++) {
      this.ecrire(
        i++,
        depart.x + a.entre(-0.05, 0.05),
        y0,
        depart.z + a.entre(-0.05, 0.05),
        vx,
        v0y,
        vz,
        j === 0 ? melangerCouleur(couleur, 0xffffff, 0.85) : melangerCouleur(0xffc06a, couleur, 0.35),
        t,
        montee,
        j === 0 ? 0.7 : a.entre(0.34, 0.5),
        g,
        k,
      )
    }

    // Trainee: des etincelles laissees le long de la trajectoire.
    for (let j = 1; j <= NB_TRAINEE; j++) {
      const tj = (j / NB_TRAINEE) * montee
      const px = integrePos(depart.x, vx, 0, k, tj)
      const py = integrePos(y0, v0y, -g, k, tj)
      const pz = integrePos(depart.z, vz, 0, k, tj)
      const vfy = integreVit(v0y, -g, k, tj)
      this.ecrire(
        i++,
        px + a.entre(-0.12, 0.12),
        py,
        pz + a.entre(-0.12, 0.12),
        a.entre(-0.7, 0.7),
        vfy * 0.12 + a.entre(-1.6, 0.4),
        a.entre(-0.7, 0.7),
        melangerCouleur(0xffb347, 0xff5a1a, a.reel()),
        t + tj,
        a.entre(0.5, 0.9),
        a.entre(0.24, 0.44),
        4,
        3,
      )
    }

    this.marquer(debut, nb)
    this.groupes.push({ fin: t + montee + 0.5, nb })
  }

  // ----------------------------------------------------------- explosions

  /** Emet les particules de l'explosion, renvoie leur duree de vie maximale. */
  private emettreExplosion(feu: FeuEnVol, t: number): number {
    switch (feu.forme) {
      case 'pivoine':
        return this.formePivoine(feu, t)
      case 'saule':
        return this.formeSaule(feu, t)
      case 'coeur':
        return this.formeCoeur(feu, t)
      case 'etoile':
        return this.formeEtoile(feu, t)
    }
  }

  /** Flash blanc de detonation, commun a toutes les formes. */
  private flash(feu: FeuEnVol, t: number): void {
    const a = this.alea
    const c = feu.centre
    const debut = this.reserver(NB_FLASH)
    for (let j = 0; j < NB_FLASH; j++) {
      // Les perles du flash sont ecartees du centre et plus petites qu'avant:
      // empilees au meme point en melange additif, elles formaient une boule
      // blanche saturee qui masquait la forme du bouquet.
      this.ecrire(
        debut + j,
        c.x + a.entre(-0.9, 0.9),
        c.y + a.entre(-0.9, 0.9),
        c.z + a.entre(-0.9, 0.9),
        a.entre(-11, 11),
        a.entre(-11, 11),
        a.entre(-11, 11),
        melangerCouleur(feu.couleur, 0xffffff, a.entre(0.45, 0.8)),
        t,
        a.entre(0.05, 0.11),
        a.entre(0.28, 0.6),
        0.7,
        1.2,
      )
    }
    this.marquer(debut, NB_FLASH)
    this.groupes.push({ fin: t + 0.2, nb: NB_FLASH })
  }

  /** Direction uniforme sur la sphere. */
  private direction(hors: THREE.Vector3): THREE.Vector3 {
    const a = this.alea
    const u = a.entre(-1, 1)
    const th = a.entre(0, Math.PI * 2)
    const s = Math.sqrt(Math.max(0, 1 - u * u))
    return hors.set(s * Math.cos(th), u, s * Math.sin(th))
  }

  /** Teinte d'une particule: melange vers le blanc, parfois vers l'or. */
  private nuance(base: number): number {
    const a = this.alea
    if (a.chance(0.12)) return melangerCouleur(base, 0xfff0c0, a.entre(0.4, 0.9))
    return melangerCouleur(base, 0xffffff, a.entre(0, 0.45))
  }

  /** Sphere reguliere, vitesses de norme quasi constante, extinction douce. */
  private formePivoine(feu: FeuEnVol, t: number): number {
    const a = this.alea
    const c = feu.centre
    const nb = 1100
    const v0 = a.entre(15, 18)
    const dureeMax = 2.5
    const debut = this.reserver(nb)
    const d = new THREE.Vector3()

    for (let j = 0; j < nb; j++) {
      this.direction(d)
      // 90 % en coquille, 10 % a l'interieur pour donner du volume.
      const norme = a.chance(0.9) ? v0 * a.entre(0.9, 1.02) : v0 * a.entre(0.3, 0.7)
      this.ecrire(
        debut + j,
        c.x,
        c.y,
        c.z,
        d.x * norme,
        d.y * norme,
        d.z * norme,
        this.nuance(feu.couleur),
        t,
        a.entre(1.9, dureeMax),
        a.entre(0.3, 0.55),
        6,
        1.7,
      )
    }
    this.marquer(debut, nb)
    this.groupes.push({ fin: t + dureeMax, nb })
    this.flash(feu, t)
    return dureeMax + 0.2
  }

  /**
   * Hemisphere haute, gravite forte, longues trainees qui retombent.
   *
   * Point important: avec un frottement et une gravite identiques pour toutes
   * les particules, une explosion ne peut QUE se dilater et glisser en bloc
   * (le deplacement se factorise en `amorti(t) * v0 + terme de gravite`), donc
   * elle ne retombe jamais en parasol. Deux ingredients cassent ca:
   *   - le frottement varie d'un rayon a l'autre, si bien que les rayons ne
   *     retombent pas de la meme hauteur;
   *   - les perles d'un meme rayon ont une NAISSANCE decalee, donc elles
   *     occupent les positions que la tete tenait un instant plus tot: la
   *     trainee suit exactement la courbe, pas une droite.
   */
  private formeSaule(feu: FeuEnVol, t: number): number {
    const a = this.alea
    const c = feu.centre
    const rayons = 120
    const parRayon = 11
    const nb = rayons * parRayon
    /** Retard entre deux perles d'un meme rayon, en secondes. */
    const retard = 0.055
    const dureeMax = 3.4
    const debut = this.reserver(nb)
    let i = debut

    for (let r = 0; r < rayons; r++) {
      // Hemisphere superieure, un peu tassee vers l'horizontale pour que la
      // gerbe s'ouvre en parasol avant de retomber.
      const dy = Math.pow(a.reel(), 0.7) * 0.9 + 0.1
      const th = a.entre(0, Math.PI * 2)
      const s = Math.sqrt(Math.max(0, 1 - dy * dy))
      const dx = s * Math.cos(th)
      const dz = s * Math.sin(th)
      const norme = a.entre(14, 18)
      const frottement = a.entre(0.5, 1.05)
      const duree = a.entre(2.7, dureeMax)

      for (let j = 0; j < parRayon; j++) {
        this.ecrire(
          i++,
          c.x,
          c.y,
          c.z,
          dx * norme,
          dy * norme,
          dz * norme,
          melangerCouleur(this.nuance(feu.couleur), 0xffb347, (j / parRayon) * 0.7),
          t + j * retard,
          // Toutes les perles d'un rayon s'eteignent ensemble.
          Math.max(0.5, duree - j * retard),
          a.entre(0.24, 0.42) * (1 - j * 0.055),
          13,
          frottement,
        )
      }
    }
    this.marquer(debut, nb)
    this.groupes.push({ fin: t + dureeMax, nb })
    this.flash(feu, t)
    return dureeMax + 0.3
  }

  /**
   * Coeur parametrique pose dans le plan face a la camera, donc lisible depuis
   * la vue a 45 degres. Le frottement est eleve pour que la forme se fige
   * avant que la gravite ne la deforme.
   */
  private formeCoeur(feu: FeuEnVol, t: number): number {
    const a = this.alea
    const c = feu.centre
    const plan = planCamera(feu.azimut, CAM_ELEVATION)
    const nb = 900
    const echelle = a.entre(19, 21)
    const dureeMax = 2.6
    const debut = this.reserver(nb)

    for (let j = 0; j < nb; j++) {
      const th = a.entre(0, Math.PI * 2)
      const st = Math.sin(th)
      // Courbe classique, normalisee puis recentree sur son barycentre.
      let hx = (16 * st * st * st) / 17
      let hy = (13 * Math.cos(th) - 5 * Math.cos(2 * th) - 2 * Math.cos(3 * th) - Math.cos(4 * th)) / 17 + 0.16
      // 15 % de remplissage interieur, le reste sur le contour.
      if (a.chance(0.15)) {
        const f = a.entre(0.25, 0.9)
        hx *= f
        hy *= f
      }
      hx += a.entre(-0.035, 0.035)
      hy += a.entre(-0.035, 0.035)

      const vx = (plan.droite.x * hx + plan.haut.x * hy) * echelle + plan.normale.x * a.entre(-0.6, 0.6)
      const vy = (plan.droite.y * hx + plan.haut.y * hy) * echelle + plan.normale.y * a.entre(-0.6, 0.6)
      const vz = (plan.droite.z * hx + plan.haut.z * hy) * echelle + plan.normale.z * a.entre(-0.6, 0.6)

      this.ecrire(
        debut + j,
        c.x,
        c.y,
        c.z,
        vx,
        vy,
        vz,
        this.nuance(feu.couleur),
        t,
        a.entre(2, dureeMax),
        a.entre(0.32, 0.52),
        4,
        2,
      )
    }
    this.marquer(debut, nb)
    this.groupes.push({ fin: t + dureeMax, nb })
    this.flash(feu, t)
    return dureeMax + 0.2
  }

  /** Etoile a 5 ou 6 branches: particules concentrees sur des rayons. */
  private formeEtoile(feu: FeuEnVol, t: number): number {
    const a = this.alea
    const c = feu.centre
    const plan = planCamera(feu.azimut, CAM_ELEVATION)
    const branches = a.chance(0.5) ? 5 : 6
    const nb = 900
    const vMax = a.entre(19, 22)
    const dureeMax = 2.3
    const debut = this.reserver(nb)
    const scratch = new THREE.Vector3()

    for (let j = 0; j < nb; j++) {
      if (a.chance(0.1)) {
        // Petit coeur lumineux au centre.
        const d = this.direction(scratch).multiplyScalar(a.entre(1, 4))
        this.ecrire(
          debut + j,
          c.x,
          c.y,
          c.z,
          d.x,
          d.y,
          d.z,
          melangerCouleur(feu.couleur, 0xffffff, 0.6),
          t,
          a.entre(1.2, 1.8),
          a.entre(0.34, 0.5),
          5,
          2.4,
        )
        continue
      }
      // Rayon: une pointe vers le haut, puis les autres reparties.
      const b = a.entier(0, branches - 1)
      // Position le long du rayon, biaisee vers l'exterieur.
      const r = 0.15 + 0.85 * Math.pow(a.reel(), 0.6)
      // Le rayon s'affine vers la pointe.
      const angle = (b * Math.PI * 2) / branches + a.entre(-0.05, 0.05) * (1.15 - r)
      const norme = vMax * r
      const ux = Math.sin(angle)
      const uy = Math.cos(angle)

      const vx = (plan.droite.x * ux + plan.haut.x * uy) * norme + plan.normale.x * a.entre(-0.5, 0.5)
      const vy = (plan.droite.y * ux + plan.haut.y * uy) * norme + plan.normale.y * a.entre(-0.5, 0.5)
      const vz = (plan.droite.z * ux + plan.haut.z * uy) * norme + plan.normale.z * a.entre(-0.5, 0.5)

      this.ecrire(
        debut + j,
        c.x,
        c.y,
        c.z,
        vx,
        vy,
        vz,
        this.nuance(feu.couleur),
        t,
        a.entre(1.7, dureeMax),
        a.entre(0.3, 0.5),
        5,
        2.1,
      )
    }
    this.marquer(debut, nb)
    this.groupes.push({ fin: t + dureeMax, nb })
    this.flash(feu, t)
    return dureeMax + 0.2
  }

  // ------------------------------------------------------------ lumieres

  /** Replace la lumiere la plus ancienne sur l'explosion. */
  private allumer(position: THREE.Vector3, couleur: number, t: number): void {
    let choisie: EtatLumiere | null = null
    for (const e of this.etats) {
      if (!e.actif) {
        choisie = e
        break
      }
      if (!choisie || e.debut < choisie.debut) choisie = e
    }
    if (!choisie) return
    choisie.debut = t
    choisie.actif = true
    choisie.light.position.copy(position)
    // Une lumiere trop saturee ne colore rien: on la deteint vers le blanc.
    this.teinteLumiere.setHex(melangerCouleur(couleur, 0xffffff, 0.35))
    choisie.light.color.copy(this.teinteLumiere)
    choisie.light.intensity = clamp(this.intensiteLumiere * 0.2, 0, 1e6)
  }
}
